import React from 'react';
import { Truck, MapPin, Clock, CheckCircle, Package } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.error || `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return data;
};

const REGIONS_KG = ['Бишкек', 'Ош', 'Чуй', 'Иссык-Куль', 'Нарын', 'Джалал-Абад', 'Баткен', 'Талас'];
const REGIONS_KZ = ['Астана', 'Алматы', 'Шымкент', 'Акмолинская', 'Актюбинская', 'Алматинская', 'Атырауская', 'Восточно-Казахстанская', 'Жамбылская', 'Жетысуская', 'Западно-Казахстанская', 'Карагандинская', 'Костанайская', 'Кызылординская', 'Мангистауская', 'Павлодарская', 'Северо-Казахстанская', 'Туркестанская', 'Улытауская'];

const deliveryStatusMeta = {
  'В обработке': { color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/35', icon: '📦' },
  'Ожидает': { color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/35', icon: '📦' },
  'Задерживается': { color: 'bg-orange-500/15 text-orange-300 border-orange-500/35', icon: '⏳' },
  'Доставлено': { color: 'bg-green-500/15 text-green-300 border-green-500/35', icon: '✅' },
  'Отменена': { color: 'bg-red-500/15 text-red-300 border-red-500/35', icon: '❌' },
};

const DeliveryPage = ({ userCountry }) => {
  const { initData, tg } = useTelegram();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const [deliveryEnabled, setDeliveryEnabled] = React.useState(false);
  const [deadlineTime, setDeadlineTime] = React.useState('');
  const [deliveryDate, setDeliveryDate] = React.useState('');
  const [readyOrders, setReadyOrders] = React.useState([]);
  const [myDeliveries, setMyDeliveries] = React.useState([]);
  const [hasActive, setHasActive] = React.useState(false);

  const [address, setAddress] = React.useState('');
  const [profileAddress, setProfileAddress] = React.useState('');
  const [useProfileAddress, setUseProfileAddress] = React.useState(true);
  const [region, setRegion] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [cancellingId, setCancellingId] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      const initDataRaw = initData?.initData;
      if (!initDataRaw) {
        setLoading(false);
        return;
      }
      try {
        const [settingsRes, ordersRes, myRes, addrRes] = await Promise.all([
          apiPost('/api/delivery/settings', { country: userCountry || 'KG' }),
          apiPost('/api/delivery/ready-orders', { initData: initDataRaw }),
          apiPost('/api/delivery/my', { initData: initDataRaw }),
          apiPost('/api/user/address/get', { initData: initDataRaw }),
        ]);
        if (!alive) return;

        setDeliveryEnabled(!!settingsRes?.enabled);
        setDeadlineTime(settingsRes?.deadlineTime || '');
        setDeliveryDate(settingsRes?.deliveryDate || '');
        setReadyOrders(Array.isArray(ordersRes?.orders) ? ordersRes.orders : []);

        const deliveries = Array.isArray(myRes?.deliveries) ? myRes.deliveries : [];
        setMyDeliveries(deliveries);
        setHasActive(deliveries.some((d) => ['В обработке', 'Ожидает', 'Задерживается'].includes(d.status)));

        const addr = addrRes?.address || '';
        setProfileAddress(addr);
        setAddress(addr);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || 'Ошибка загрузки');
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [initData?.initData, userCountry]);

  const handleSubmit = async () => {
    setSubmitError('');
    const finalAddress = useProfileAddress ? profileAddress : address;
    if (!finalAddress?.trim()) {
      setSubmitError('Укажите адрес доставки');
      return;
    }
    if (!region) {
      setSubmitError('Выберите регион доставки');
      return;
    }

    const initDataRaw = initData?.initData;
    if (!initDataRaw) {
      setSubmitError('Telegram initData ещё не готов');
      return;
    }

    setSubmitting(true);
    try {
      const createRes = await apiPost('/api/delivery/create', { initData: initDataRaw, address: finalAddress.trim(), region });
      if (createRes?.deliveryDate) setDeliveryDate(createRes.deliveryDate);
      setSubmitSuccess(true);
      // Refresh deliveries
      const myRes = await apiPost('/api/delivery/my', { initData: initDataRaw });
      const deliveries = Array.isArray(myRes?.deliveries) ? myRes.deliveries : [];
      setMyDeliveries(deliveries);
      setHasActive(true);
    } catch (e) {
      setSubmitError(e?.message || 'Ошибка оформления');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg text-text-primary flex items-center justify-center p-6">
        <div className="text-sm text-text-secondary">Загрузка…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-text-primary pb-20">
      <div className="px-4 pt-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-2xl border border-stroke bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
            <Truck size={22} className="text-neon-blue" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Доставка</h1>
            <p className="text-xs text-text-secondary">Оформление и отслеживание</p>
          </div>
        </div>

        {error ? <div className="mb-4 text-xs text-red-300">{error}</div> : null}

        {/* Delivery window status */}
        <div className="rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className={deliveryEnabled ? 'text-green-400' : 'text-text-muted'} />
            <span className="text-sm font-semibold">
              {deliveryEnabled ? 'Доставка доступна' : 'Доставка недоступна'}
            </span>
          </div>
          {deliveryEnabled ? (
            <p className="text-xs text-text-secondary">
              {userCountry === 'KZ'
                ? 'Доставка доступна. Оформите доставку — все ваши заказы «Готов к выдаче» будут доставлены.'
                : <>Оформите доставку до <span className="text-text-primary font-semibold">{deadlineTime}</span> (Бишкек). Все ваши заказы «Готов к выдаче» будут доставлены.</>
              }
            </p>
          ) : (
            <p className="text-xs text-text-secondary">
              Менеджер ещё не открыл окно доставки. Вы получите уведомление, когда доставка станет доступна.
            </p>
          )}
        </div>

        {/* Order delivery form */}
        {deliveryEnabled && readyOrders.length > 0 && !hasActive && !submitSuccess ? (
          <div className="rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5 mb-4">
            <div className="text-sm font-semibold mb-3">Оформить доставку</div>

            <div className="mb-3">
              <div className="text-xs text-text-muted mb-1">Заказы для доставки ({readyOrders.length})</div>
              <div className="space-y-1">
                {readyOrders.map((o) => (
                  <div key={o.track_number} className="flex items-center gap-2 text-xs">
                    <Package size={12} className="text-neon-blue shrink-0" />
                    <code className="text-text-primary">{o.track_number}</code>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <div className="text-xs text-text-muted mb-2">Адрес доставки</div>
              {profileAddress ? (
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={useProfileAddress}
                      onChange={() => { setUseProfileAddress(true); setAddress(profileAddress); }}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="text-xs text-text-primary font-medium">Адрес из профиля</div>
                      <div className="text-xs text-text-secondary">{profileAddress}</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!useProfileAddress}
                      onChange={() => setUseProfileAddress(false)}
                      className="mt-0.5"
                    />
                    <div className="text-xs text-text-primary font-medium">Другой адрес</div>
                  </label>
                  {!useProfileAddress ? (
                    <input
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Введите адрес доставки"
                      className="w-full rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-sm text-white placeholder:text-white/45 outline-none focus:border-white/25"
                    />
                  ) : null}
                </div>
              ) : (
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Введите адрес доставки"
                  className="w-full rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-sm text-white placeholder:text-white/45 outline-none focus:border-white/25"
                />
              )}
            </div>

            <div className="mb-3">
              <div className="text-xs text-text-muted mb-2">Регион доставки <span className="text-red-400">*</span></div>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="w-full rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-sm text-white outline-none focus:border-white/25"
              >
                <option value="">Выберите регион</option>
                {(userCountry === 'KZ' ? REGIONS_KZ : REGIONS_KG).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            {submitError ? <div className="mb-3 text-xs text-red-300">{submitError}</div> : null}

            <button
              type="button"
              disabled={submitting || !region}
              onClick={handleSubmit}
              className="w-full py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15] text-white hover:brightness-110 transition disabled:opacity-60"
            >
              {submitting ? 'Оформление…' : 'Оформить доставку'}
            </button>
          </div>
        ) : null}

        {submitSuccess ? (
          <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-5 mb-4 text-center">
            <CheckCircle size={32} className="text-green-400 mx-auto mb-2" />
            <div className="text-sm font-semibold text-green-300">Доставка оформлена!</div>
            <p className="text-xs text-text-secondary mt-1">Курьер доставит ваш заказ <span className="text-text-primary font-semibold">{deliveryDate ? new Date(deliveryDate + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</span>.</p>
          </div>
        ) : null}

        {deliveryEnabled && readyOrders.length === 0 && !hasActive && !submitSuccess ? (
          <div className="rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5 mb-4 text-center">
            <div className="text-xs text-text-secondary">У вас нет заказов со статусом «Готов к выдаче».</div>
          </div>
        ) : null}

        {/* My deliveries */}
        {myDeliveries.length > 0 ? (
          <div className="rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5">
            <div className="text-sm font-semibold mb-3">Мои доставки</div>
            <div className="space-y-3">
              {myDeliveries.map((d) => {
                const meta = deliveryStatusMeta[d.status] || deliveryStatusMeta['Ожидает'];
                return (
                  <div key={d.id} className="rounded-xl border border-stroke bg-white/3 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-lg border ${meta.color}`}>
                        {meta.icon} {d.status}
                      </span>
                      <span className="text-[11px] text-text-muted">#{d.id}</span>
                    </div>
                    <div className="text-xs text-text-secondary space-y-1">
                      <div className="flex items-start gap-1.5">
                        <MapPin size={12} className="text-text-muted shrink-0 mt-0.5" />
                        <span>{d.address}</span>
                      </div>
                      <div>Дата доставки: <span className="text-text-primary">{d.delivery_date || '—'}</span></div>
                      <div className="text-[11px] text-text-muted">Создано: {d.created_at || ''}</div>
                    </div>
                    {/* Обратная совместимость: можно отменять 'В обработке' и старый 'Ожидает' */}
                    {(d.status === 'В обработке' || d.status === 'Ожидает') ? (
                      <button
                        type="button"
                        disabled={cancellingId === d.id}
                        onClick={async () => {
                          const initDataRaw = initData?.initData;
                          if (!initDataRaw) return;
                          const ok = tg?.showPopup
                            ? await new Promise((resolve) => tg.showPopup({ title: 'Подтверждение', message: 'Отменить доставку?', buttons: [{ id: 'cancel', type: 'cancel', text: 'Нет' }, { id: 'ok', type: 'destructive', text: 'Отменить' }] }, (bid) => resolve(bid === 'ok')))
                            : window.confirm('Отменить доставку?');
                          if (!ok) return;
                          setCancellingId(d.id);
                          try {
                            await apiPost('/api/delivery/cancel', { initData: initDataRaw, deliveryId: d.id });
                            const myRes = await apiPost('/api/delivery/my', { initData: initDataRaw });
                            const dels = Array.isArray(myRes?.deliveries) ? myRes.deliveries : [];
                            setMyDeliveries(dels);
                            setHasActive(dels.some((x) => ['В обработке', 'Ожидает', 'Задерживается'].includes(x.status)));
                          } catch (_) {}
                          setCancellingId(null);
                        }}
                        className="mt-2 w-full py-2 rounded-xl text-xs font-semibold border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition disabled:opacity-60"
                      >
                        {cancellingId === d.id ? 'Отмена…' : 'Отменить доставку'}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DeliveryPage;

